import {createReducer, on} from '@ngrx/store';
import * as ResearchPaperActions from './research-paper.actions';
import {ResearchPaperDTO} from '../../dto/research-paper';

export interface ResearchPaperState {
  papersByProject: { [projectId: number]: ResearchPaperDTO[] };
  searched: { [projectId: number]: boolean };
  loading: boolean;
  errorByProject: { [projectId: number]: any };
  selectedPaper: ResearchPaperDTO | null;
  screeningRunning: boolean;
}

export const initialState: ResearchPaperState = {
  papersByProject: {},
  searched: {},
  loading: false,
  errorByProject: {},
  selectedPaper: null,
  screeningRunning: false,
};

export const researchPaperReducer = createReducer(
  initialState,

  on(ResearchPaperActions.loadResearchPapers, (state, {projectId}) => ({
    ...state,
    loading: true,
    errorByProject: {...state.errorByProject, [projectId]: null},
  })),
  on(ResearchPaperActions.startPaperResearchComplete, (state, {projectId, papers}) => ({
    ...state,
    searched: {...state.searched, [projectId]: true},
    papersByProject: {
      ...state.papersByProject,
      [projectId]: [
        ...(state.papersByProject[projectId] || []).filter(paper => !papers.some(newPaper => newPaper.id === paper.id)),
        ...papers,
      ]
    },
  })),
  on(ResearchPaperActions.loadResearchPapersSuccess, (state, {projectId, papers}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: [
        ...(state.papersByProject[projectId] || []).filter(paper => !papers.some(newPaper => newPaper.id === paper.id)),
        ...papers,
      ]
    },
    loading: false,
  })),
  on(ResearchPaperActions.loadResearchPapersFailure, (state, {projectId, error}) => ({
    ...state,
    loading: false,
    errorByProject: {...state.errorByProject, [projectId]: error},
  })),

  on(ResearchPaperActions.createResearchPaperSuccess, (state, {projectId, paper}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: [
        ...(state.papersByProject[projectId] || []).filter(p => p.id !== paper.id),
        paper],
    },
  })),
  on(ResearchPaperActions.createResearchBulkPaperSuccess, (state, {projectId, papers}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: [
        ...(state.papersByProject[projectId] || []),
        ...papers],
    },
  })),
  on(ResearchPaperActions.deleteResearchPaperSuccess, (state, {projectId, id}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: state.papersByProject[projectId]?.filter((p) => p.id !== id) || [],
    },
  })),
  on(ResearchPaperActions.deleteAllResearchPaperSuccess,
    ResearchPaperActions.clearPaperStore,
    (state, {projectId}) => ({
      ...state,
      papersByProject: {
        ...state.papersByProject,
        [projectId]: [],
      },
    })),
  on(ResearchPaperActions.retrieveResearchPaper, (state, {projectId}) => ({
    ...state,
    loading: true,
    errorByProject: {...state.errorByProject, [projectId]: null},
  })),
  on(ResearchPaperActions.retrieveResearchPaperSuccess, (state, {paper}) => ({
    ...state,
    selectedPaper: paper,
    loading: false,
  })),
  on(ResearchPaperActions.retrieveResearchPaperFailure, (state, {projectId, error}) => ({
    ...state,
    errorByProject: {...state.errorByProject, [projectId]: error},
    loading: false,
  })),

  on(ResearchPaperActions.updateResearchPaperSuccess, (state, {projectId, paper}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: state.papersByProject[projectId]?.map((p) =>
        p.id === paper.id ? paper : p
      ) || [],
    },
    selectedPaper: (state.selectedPaper != null && state.selectedPaper.id === paper.id) ? paper : state.selectedPaper,
  })),
  on(ResearchPaperActions.createManuelScreeningResearchPaperSuccess, (state, {projectId, paperId, screening}) => ({
    ...state,
    papersByProject: {
      ...state.papersByProject,
      [projectId]: state.papersByProject[projectId]?.map((p) =>
        p.id === paperId ? {...p, latestScreeningRun: screening} : p
      ) || [],
    },
    selectedPaper: (state.selectedPaper != null && state.selectedPaper.id === paperId) ?
      {...state.selectedPaper, latestScreeningRun: screening}
      : state.selectedPaper,
  })),
  on(ResearchPaperActions.startPaperScreening, (state, {projectId, screeningRunSettings}) => ({
    ...state,
    screeningRunning: true,
    papersByProject: screeningRunSettings?.ignoreScreened
      ? state.papersByProject
      : {
        ...state.papersByProject,
        [projectId]:
        state.papersByProject[projectId]?.map((p) => ({
          ...p,
          oldScreeningProcesses: p.latestScreeningRun ? [...p.oldScreeningProcesses || [], p.latestScreeningRun] : [],
          latestScreeningRun: undefined
        })) || []
      },
  })),
  on(ResearchPaperActions.watchPaperScreening, (state) => ({
    ...state,
    screeningRunning: true,
  })),
  on(
    ResearchPaperActions.screeningNotRunning,
    ResearchPaperActions.paperScreeningFailure,
    ResearchPaperActions.startPaperScreeningComplete,
    ResearchPaperActions.stopScreeningSuccess,
    ResearchPaperActions.startPaperScreeningCompleteOnePaper, (state) => ({
      ...state,
      screeningRunning: false,
    })),);
